import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, Inject, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';

import { DomController, IonContent, mdTransitionAnimation, ViewDidEnter, ViewWillEnter, ViewWillLeave } from '@ionic/angular';

import { SplashScreenPlugin } from '../../core/services/native-plugins/splash-screen.plugin';

import { combineLatest, forkJoin, of, Subject } from 'rxjs';
import { debounceTime, delay, filter, skip, startWith, take, takeUntil, withLatestFrom } from 'rxjs/operators';

import { AreaSelectionModalPage, AreaSelectionModalPageIdentifier } from '../../modals/area-selection-modal/area-selection-modal.page';
import { EditWishlistModalPage, EditWishlistModalPageIdentifier } from '../../modals/edit-wishlist-modal/edit-wishlist-modal.page';
import {
  OrderDetailsModalPage,
  OrderDetailsModalPageIdentifier,
  OrderDetailsModalPageResult,
} from '../../modals/order-details-modal/order-details-modal.page';
import {
  OrderPaymentModalPage,
  OrderPaymentModalPageIdentifier,
  OrderPaymentModalResult,
} from '../../modals/order-payment-modal/order-payment-modal.page';
import { TagsModalPage, TagsModalPageIdentifier, TagsModalResult } from '../../modals/tags-modal/tags-modal.page';
import { VendorReviewsModalPage, VendorReviewsModalPageIdentifier } from '../../modals/vendor-reviews-modal/vendor-reviews-modal.page';

import { Availability } from '../../core/enums/availability.enum';
import { CanPayCashOrderResult } from '../../core/enums/can-pay-cash-order-result.enum';
import { HighlightedCollectionType } from '../../core/enums/highlighted-collection-type.enum';
import { TargetPage } from '../../core/enums/target-page.enum';
import { VendorSorterType } from '../../core/enums/vendor-sorter-type.enum';

import { ActiveOrderVendor } from '../../core/models/active-order-vendor.model';
import { Address } from '../../core/models/address.model';
import { Banner } from '../../core/models/banner.model';
import { CatererRating } from '../../core/models/caterer-rating.model';
import { CountryCode } from '../../core/models/country.model';
import { CustomScrollEvent, ScrollEventDetail } from '../../core/models/custom-scroll-event.model';
import { GroceryMenuItem } from '../../core/models/grocery-menu-item.model';
import { GroceryVendorDetails } from '../../core/models/grocery-vendor-details.model';
import { HighlightedCollection } from '../../core/models/highlighted-collection.model';
import { InfoMessage } from '../../core/models/info-message.model';
import { LanguageCode } from '../../core/models/language.model';
import { LoyaltyProgramDetails } from '../../core/models/loyalty-program-details.model';
import { PushNotification } from '../../core/models/push-notification.model';
import { ServiceTypeOption } from '../../core/models/service-type-option.model';
import { Tag } from '../../core/models/tag.model';
import { UniversalLink } from '../../core/models/universal-link.model';
import { VendorDetails } from '../../core/models/vendor-details.model';
import { VendorItem } from '../../core/models/vendor-item.model';
import { Vendor } from '../../core/models/vendor.model';

import { AccountService } from '../../core/services/account.service';
import { AddressService } from '../../core/services/address.service';
import { AnalyticsService } from '../../core/services/analytics.service';
import { AppEventsService } from '../../core/services/app-events.service';
import { AppService } from '../../core/services/app.service';
import { AreasService } from '../../core/services/areas.service';
import { BackgroundService } from '../../core/services/background.service';
import { CartService } from '../../core/services/cart.service';
import { DateTimeService } from '../../core/services/date-time.service';
import { DeepLinkService } from '../../core/services/deep-link.service';
import { LazyLoadingImagesService } from '../../core/services/lazy-loading-images.service';
import { LoggerService } from '../../core/services/logger.service';
import { LoyaltyProgramService } from '../../core/services/loyalty-program.service';
import { ModalService } from '../../core/services/modal.service';
import { NavigationService } from '../../core/services/navigation.service';
import { OrdersService } from '../../core/services/orders.service';
import { OverlayService } from '../../core/services/overlay.service';
import { PushNotificationsService } from '../../core/services/push-notifications.service';
import { ScrollService } from '../../core/services/scroll.service';
import { SettingsService } from '../../core/services/settings.service';
import { PlatformService } from '../../core/services/ssr/platform.service';
import { UniversalLinksService } from '../../core/services/universal-links.service';
import { ValidationService } from '../../core/services/validation.service';
import { VendorsService } from '../../core/services/vendors.service';

import { AnalyticsConfig, TOKEN_ANALYTICS_CONFIG } from '../../core/config/analytics.config';

import {
  GroceryItemDetailsModalComponent,
  GroceryItemDetailsModalPageIdentifier,
  GroceryItemDetailsModalParams,
  GroceryItemDetailsModalResult,
} from '../../modals/grocery-item-details-modal/grocery-item-details-modal.component';
import {
  MenuItemDetailsModalComponent,
  MenuItemDetailsModalPageIdentifier,
  MenuItemDetailsModalParams,
  MenuItemDetailsModalResult,
} from '../../modals/menu-item-details-modal/menu-item-details-modal.component';
import { AreaDateTimePickerComponentResult } from '../../shared/components/area-date-time-picker/area-date-time-picker.component';
import { BannerAdsComponent } from '../../shared/components/banner-ads/banner-ads.component';
import { FeaturedCollectionHorizontalListComponent } from '../../shared/components/featured-collection-horizontal-list/featured-collection-horizontal-list.component';

import { ErrorPageParams } from '../error/error-routing.module';

import { Helpers } from '../../core/helpers';

import { VendorsHomePageAnimations } from './vendors-home.animations';

import SwiperCore, { Navigation, SwiperOptions } from 'swiper';
import { toPromise } from '../../core/operators/to-promise';

SwiperCore.use([Navigation]);

const MIN_TIME_TO_REFRESH_RESULTS_FOR_LATER_IN_MINUTES = 2880;
const MIN_TIME_TO_REFRESH_RESULTS_FOR_NOW_IN_MINUTES = 30;

const MORE_FILTERS_POPULAR_TAG_ID = -1;
const POPULAR_TAGS_MIN_QUANTITY_FOR_TWO_ROWS = 6;

const HEADER_EXPANDING_VELOCITY_LIMIT = 0.75;
const HEADER_COLLAPSING_VELOCITY_LIMIT = 0.75;
const VENDORS_PAGE_SIZE = 20;
const INFINITE_SCROLL_DURATION_IN_MS = 500;

const SCROLL_FIRST_UI_BREAKPOINT_IN_PX = 100;
const SCROLL_SECOND_UI_BREAKPOINT_IN_PX = 150;
const SCROLL_THIRD_UI_BREAKPOINT_IN_PX = 500;

interface VendorsOrItems {
  vendors: Array<Vendor>;
  vendorsItems: Array<VendorItem>;
}

@Component({
  selector: 'app-vendors-home',
  templateUrl: './vendors-home.page.html',
  styleUrls: ['./vendors-home.page.scss'],
  animations: [...VendorsHomePageAnimations],
  encapsulation: ViewEncapsulation.None,
})
export class VendorsHomePage implements OnInit, ViewWillEnter, ViewDidEnter, ViewWillLeave, OnDestroy {
  @ViewChild(IonContent)
  public content: IonContent;

  @ViewChildren('serviceTypeFilter', { read: ElementRef })
  public serviceTypeFilters: QueryList<ElementRef<HTMLElement>>;

  @ViewChild(BannerAdsComponent)
  public bannersComponent: BannerAdsComponent;

  @ViewChildren(FeaturedCollectionHorizontalListComponent)
  public featuredCollectionsComponents: QueryList<FeaturedCollectionHorizontalListComponent>;

  @ViewChild('results', { static: false, read: ElementRef })
  public resultsElement: ElementRef<HTMLElement>;

  public isLoggedIn: boolean;
  public selectedAddress: Address;
  public selectedDateTime: string;

  public enableCompactView = true;
  public selectedServiceType: number;
  public selectedServiceTypeName: string;
  public isOrderForNowSelected: boolean;
  public isSelectedServiceTypeBasedOnDateTime: boolean;
  public isSelectedServiceTypeDeliveredByBilbayt = false;

  public isLoading = true;
  public isBrowser: boolean;

  public allResults: VendorsOrItems;
  public filteredResults: VendorsOrItems;
  public visibleResults: VendorsOrItems;

  public tags: Array<Tag>;
  public popularTags: { firstRow: Array<Tag>; secondRow: Array<Tag> };
  public hasSelectedTags = false;
  public selectedTagsIds: Array<number>;
  public isRecommendedSorterSelected = true;
  public selectedSorterType: VendorSorterType;

  public collections: Array<HighlightedCollection>;
  public infoMessages: Array<InfoMessage>;
  public serviceTypeOptions: Array<ServiceTypeOption>;

  public banners: Array<Banner>;
  public isPageInitialized = false;

  public shouldShowLoyaltyProgramDetails: boolean;
  public loyaltyProgramDetails: LoyaltyProgramDetails;
  public isReloadingLoyaltyProgramDetails: boolean;
  public isLoggedInOnLoyaltyProgramStatusInit: boolean;
  public orderCountOnLoyaltyProgramStatusInit: number;
  public upcomingOrdersVendorsCount: number;

  public showViewModeButton = false;
  public showHeaderFiltersButton = false;
  public showHeaderDateTimeButton = false;
  public showServiceTypeFiltersScrollButton = false;

  public globalHeaderServiceTypesSliderConfig: SwiperOptions;
  public globalHeaderServiceTypesSliderDirection: string;

  public MORE_FILTERS_POPULAR_TAG_ID = MORE_FILTERS_POPULAR_TAG_ID;
  public Availability: typeof Availability = Availability;
  public HighlightedCollectionType: typeof HighlightedCollectionType = HighlightedCollectionType;
  public CanPayCashOrderResult: typeof CanPayCashOrderResult = CanPayCashOrderResult;

  private currentY: number;
  private isHeaderExpanded = true;
  private scrollElement: HTMLElement;
  private mainHeaderElement: HTMLElement;
  private headerItemOneElement: HTMLElement;
  private headerItemTwoElement: HTMLElement;
  private headerItemThreeElement: HTMLElement;

  private currentCountryCode: CountryCode;
  private currentLanguageCode: LanguageCode;

  private currentPageNumber = 0;
  private resetFeaturedItemHorizontalScroll = true;
  private selectedServiceTypeSupportsVendorsItemsOnHomePage: boolean;
  private lazyLoadingImagesObserversIds: Array<string> = [];

  private isPageActive$ = new Subject<boolean>();
  private unsubscribe$ = new Subject<void>();

  constructor(
    private activatedRoute: ActivatedRoute,
    private appService: AppService,
    private appEventsService: AppEventsService,
    private accountService: AccountService,
    private modalService: ModalService,
    private elementRef: ElementRef<HTMLElement>,
    private domController: DomController,
    private areasService: AreasService,
    private navigationService: NavigationService,
    private overlayService: OverlayService,
    private validationService: ValidationService,
    private cartService: CartService,
    private settingsService: SettingsService,
    private platformService: PlatformService,
    private vendorsService: VendorsService,
    private dateTimeService: DateTimeService,
    private analyticsService: AnalyticsService,
    private loggerService: LoggerService,
    private backgroundService: BackgroundService,
    private translateService: TranslateService,
    private addressService: AddressService,
    private lazyLoadingImagesService: LazyLoadingImagesService,
    private ordersService: OrdersService,
    private scrollService: ScrollService,
    private splashScreenPlugin: SplashScreenPlugin,
    private deepLinkService: DeepLinkService,
    private universalLinksService: UniversalLinksService,
    private pushNotificationsService: PushNotificationsService,
    private loyaltyProgramService: LoyaltyProgramService,
    @Inject(TOKEN_ANALYTICS_CONFIG) private analyticsConfig: AnalyticsConfig,
  ) {}

  ngOnInit() {
    this.isBrowser = this.platformService.isBrowser;

    // We already show the loyalty program details in the home page
    // when running the app on mobile devices
    this.shouldShowLoyaltyProgramDetails = this.isBrowser;

    this.globalHeaderServiceTypesSliderDirection = this.settingsService.getLanguageDirection();
    this.globalHeaderServiceTypesSliderConfig = {
      slidesPerView: 'auto',
      freeMode: true,
      navigation: {
        nextEl: '#next',
        prevEl: '#prev',
      },
    };

    // In the mobile app deep links in general are handled in the HomePage, so we need
    // to listen to these events here only if we're in the browser since the HomePage
    // doesn't exist
    if (this.isBrowser) {
      this.pushNotificationsService.handlePushNotificationOnSearchResultsPage$
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((notification) => this.handlePushNotificationDeepLink(notification));

      this.universalLinksService.handleUniversalLinkOnSearchResultsPage$
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((universalLink) => this.handleUniversalLinkDeepLink(universalLink));
    }

    if (this.shouldShowLoyaltyProgramDetails) {
      this.ordersService.activeOrders$.pipe(takeUntil(this.unsubscribe$)).subscribe((activeOrdersDetails) => {
        this.upcomingOrdersVendorsCount = activeOrdersDetails.vendors.length || 0;

        const orderCountChangedSinceInit = this.orderCountOnLoyaltyProgramStatusInit !== this.upcomingOrdersVendorsCount;
        const loggedInStatusChangedSinceInit = this.isLoggedInOnLoyaltyProgramStatusInit !== this.accountService.isLoggedIn();

        // If the logged in status changed we don't need to do anything
        // because the entire page will be initialized again
        if (this.loyaltyProgramDetails && orderCountChangedSinceInit && !loggedInStatusChangedSinceInit) {
          this.reloadLoyaltyProgramDetails();
        }
      });
    }

    combineLatest([
      // CombineLatest would emit only if both sources emit at least once, so we must
      // use startWith with the current values
      this.activatedRoute.fragment.pipe(startWith(this.activatedRoute.snapshot.fragment)),
      this.activatedRoute.queryParamMap.pipe(startWith(this.activatedRoute.snapshot.queryParamMap)),
    ])
      .pipe(
        // Skip the first emission because it's the one we triggered
        // by using startWith in the sources
        skip(1),
        filter(() => this.isPageInitialized),
        debounceTime(100),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(([fragment, queryParamMap]) => {
        this.handleUrlChanges(fragment, queryParamMap);
      });

    // There are two issues solved by this code
    // https://bilbayt.atlassian.net/browse/CA-2919
    // 1) Log in: as part of the loyalty program feature we now allow users to sign in directly from this page
    //    if the widget is disabled but the ionViewWillEnter hook won't be executed until re-entering the page
    // 2) Log out: in both the mobile app and the website the user could be logged out on any page
    //    (for example because of a 401 error) but again nothing will be updated until re-entering the page
    this.appEventsService
      .onEvent('UserLoginStatusChanged')
      .pipe(withLatestFrom(this.isPageActive$), takeUntil(this.unsubscribe$))
      .subscribe(([_, isPageActive]) => {
        const hasChangedLoggedInStatus = this.isLoggedIn !== this.accountService.isLoggedIn();
        if (hasChangedLoggedInStatus && isPageActive) {
          this.initializePage();
        }
      });
  }

  ionViewWillEnter() {
    const hasChangedLoggedInStatus = this.isLoggedIn !== this.accountService.isLoggedIn();
    const hasChangedCountryOrLanguage =
      this.currentCountryCode !== this.settingsService.getCountry().code ||
      this.currentLanguageCode !== this.settingsService.getLanguage()?.value;

    if (!this.isPageInitialized || hasChangedLoggedInStatus || hasChangedCountryOrLanguage) {
      this.initializePage();
      return;
    }

    this.serviceTypeOptions = this.settingsService.getServiceTypeOptions();

    // Wait for a few ms to avoid opening a deep link while the page
    // is still not ready (we're in the willEnter hook), otherwise
    // the page being opened may be broken
    void this.platformService.wait(500).then(() => {
      const canContinueAfterDeepLinkCheck = this.maybeHandleDeepLinks();
      if (!canContinueAfterDeepLinkCheck) {
        return;
      }

      const canContinueAfterBackgroundCheck = this.maybeHandleAppBeingInBackgroundForTooLong();
      if (!canContinueAfterBackgroundCheck) {
        return;
      }

      this.maybeUpdateResultsAfterAddingItemToCart();
    });
  }

  ionViewDidEnter() {
    this.isPageActive$.next(true);

    this.trackPageView();
    this.splashScreenPlugin.hide();
  }

  ionViewWillLeave() {
    this.isPageActive$.next(false);

    // Dismiss any active overlays if a push notification is sent
    // and the user taps to open it
    void this.modalService.dismissTopModal();
    this.overlayService.hideAreaDateTimePicker();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
    this.lazyLoadingImagesObserversIds.forEach((observerId) => this.lazyLoadingImagesService.disconnectObserver(observerId));
  }

  public onScroll(event: Event): void {
    if (event) {
      this.handleScrollEvent((event as CustomScrollEvent).detail);
    }
  }

  public onRedirectToHomePage(): void {
    void this.navigationService.navigateBackTo('/vendors/home', mdTransitionAnimation);
  }

  public onSelectBanner(banner: Banner): void {
    if (banner.url) {
      void this.overlayService.showLoading().then(() => {
        this.universalLinksService.getDataFromUniversalLinkUrl(banner.url).subscribe({
          next: (universalLinkData: UniversalLink) => {
            void this.overlayService.hideLoading();

            const universalLink = this.universalLinksService.getUniversalLinkFromLinkData(universalLinkData, 'bannerAd');

            if (universalLink) {
              void this.universalLinksService.handleUniversalLink(universalLink);
            }
          },
          error: (error: unknown) => {
            const errorMessage = this.translateService.instant('MODAL_AD_PAGE.OPEN_LINK_ERROR_MESSAGE') as string;
            this.loggerService.info({
              component: 'VendorsHomePage',
              message: "couldn't get data from universal link url",
              details: { error, messageShownToUser: errorMessage },
            });

            void this.overlayService
              .hideLoading()
              .then(() => this.overlayService.showToast({ message: errorMessage, showCloseButton: true, type: 'error' }));
          },
        });
      });
    }
  }

  public onOpenSearchPage(): void {
    void this.navigationService.navigatePreservingFragmentAndQueryParamsTo('/search', false, {
      animation: mdTransitionAnimation,
    });
  }

  public onOpenVendorReviews(vendorId: number, rating: CatererRating): void {
    void this.overlayService.showLoading().then(() => {
      this.vendorsService
        .getReviews(vendorId)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe({
          next: (reviews) => {
            void this.overlayService.hideLoading();
            void this.modalService.showCard({
              component: VendorReviewsModalPage,
              id: VendorReviewsModalPageIdentifier,
              presentingElement: this.elementRef.nativeElement,
              componentProps: { vendorId, rating, reviews },
            });
          },
          error: (error: HttpErrorResponse) => {
            const errorMessage = this.validationService.getErrorMessage(error);
            this.loggerService.info({
              component: 'VendorsHomePage',
              message: "couldn't get vendor reviews",
              details: { error, messageShownToUser: errorMessage },
            });

            void this.overlayService
              .hideLoading()
              .then(() => this.overlayService.showToast({ type: 'error', message: errorMessage, showCloseButton: true }));
          },
        });
    });
  }

  public onServiceTypeFiltersScroll(event: Event): void {
    const eventTarget = event.target as HTMLElement;

    if (eventTarget) {
      const scrollWidth = eventTarget.scrollWidth;
      const scrollLeft = Math.abs(eventTarget.scrollLeft);
      const showServiceTypeFiltersScrollButton = scrollLeft < scrollWidth - this.platformService.width() - 16;

      if (this.showServiceTypeFiltersScrollButton !== showServiceTypeFiltersScrollButton) {
        this.showServiceTypeFiltersScrollButton = showServiceTypeFiltersScrollButton;
      }
    }
  }

  public onToggleCompactView(): void {
    this.enableCompactView = !this.enableCompactView;
  }

  public onTogglePopularTag(selectedTag: Tag): void {
    if (selectedTag.tagId === MORE_FILTERS_POPULAR_TAG_ID) {
      this.onShowTagsModalPage();
      return;
    }

    const existingTag = this.tags.find((tag) => tag.tagId === selectedTag.tagId);

    if (existingTag) {
      existingTag.selected = !existingTag.selected;

      void this.scrollToTop().then(() => {
        void this.overlayService.showLoading().then(() => {
          this.updateTags(this.tags);
          this.updateAndSortResultsBasedOnSelectedFilters();
          void this.overlayService.hideLoading();
        });
      });
    }
  }

  public onServiceTypeChange(serviceType: number): void {
    const newSearchFilters = this.vendorsService.getDatetimeSearchFilters(serviceType);
    this.navigationService.updateServiceTypeFromUrl(
      this.activatedRoute,
      serviceType,
      newSearchFilters.dateTime,
      newSearchFilters.orderForNow,
    );
  }

  public onShowTagsModalPage(): void {
    void this.modalService
      .showCard<TagsModalResult>({
        component: TagsModalPage,
        id: TagsModalPageIdentifier,
        presentingElement: this.elementRef.nativeElement,
        componentProps: {
          tags: Helpers.clone(this.tags),
          selectedSorterType: this.selectedSorterType,
        },
      })
      .then((filters) => {
        if (filters) {
          // 0 is a valid sorterType
          const changedSorterType = filters.sorterType != null;
          const changedTags = filters.tags;

          if (changedSorterType) {
            this.updateSorterType(filters.sorterType);
          }

          if (changedTags) {
            this.updateTags(filters.tags);
          }

          if (changedSorterType || changedTags) {
            void this.scrollToTop().then(() => {
              void this.overlayService.showLoading().then(() => {
                this.updateAndSortResultsBasedOnSelectedFilters();
                void this.overlayService.hideLoading();
              });
            });
          }
        }
      });
  }

  public onScrollServiceTypeTabs(): void {
    if (!this.showServiceTypeFiltersScrollButton) {
      // Since the chevron button is on top of the last service type tab, we
      // should propagate the tap to that tab if the button is disabled
      const lastTabServiceType = this.serviceTypeOptions[this.serviceTypeOptions.length - 1].serviceTypes;
      this.onServiceTypeChange(lastTabServiceType);

      return;
    }

    const lastTab = this.serviceTypeFilters.toArray().slice(-1)[0];

    if (lastTab?.nativeElement) {
      void this.scrollService.scrollHorizontallyToSelectedTab(lastTab.nativeElement);
    }
  }

  public onLoadMoreVendors(event?: Event): void {
    const vendorsNextPage = this.getNextPageOfResults(this.filteredResults);

    this.visibleResults.vendors.push(...vendorsNextPage.vendors);
    this.visibleResults.vendorsItems.push(...vendorsNextPage.vendorsItems);

    if (vendorsNextPage.vendors.length || vendorsNextPage.vendorsItems.length) {
      void this.initializelazyLoadingImagesObserver();
    }

    void this.platformService.wait(INFINITE_SCROLL_DURATION_IN_MS).then(() => {
      const scrollElement = event?.target as HTMLIonInfiniteScrollElement;
      if (typeof scrollElement?.complete === 'function') {
        void scrollElement.complete();
      }
    });
  }

  public onAddNewWishlist(): void {
    void this.modalService.showModal<string>({ component: EditWishlistModalPage, id: EditWishlistModalPageIdentifier }).then((newName) => {
      if (newName) {
        void this.overlayService.showLoading().then(() => {
          this.accountService
            .createWishlist(newName)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
              next: () => {
                void this.updateWishlistsCollection().then(() => {
                  const message = this.translateService.instant('NEW_LIST_CREATED', { name: newName }) as string;
                  this.overlayService.showToast({ message, type: 'success' });
                  void this.overlayService.hideLoading();
                });
              },
              error: (error: HttpErrorResponse) => {
                const errorMessage = this.validationService.getErrorMessage(error);
                this.loggerService.info({
                  component: 'VendorsHomePage',
                  message: "couldn't create new wish list",
                  details: { error, messageShownToUser: errorMessage },
                });

                void this.overlayService.hideLoading().then(() => {
                  this.overlayService.showToast({ message: errorMessage, showCloseButton: true, type: 'error' });
                });
              },
            });
        });
      }
    });
  }

  public onOpenAreaModal(): void {
    if (this.isLoading) {
      return;
    }

    void this.showAreaModal().then((address) => {
      if (!address) {
        return;
      }

      if (this.addressService.hasLocationChanged(this.selectedAddress, address)) {
        this.vendorsService.setAddressSearchFilters(address);
        this.navigationService.updateAreaIdFromUrl(this.activatedRoute, address.area.areaId);
      }
    });
  }

  public onOpenDateTimePicker({ trackEvent }: { trackEvent: boolean } = { trackEvent: false }): void {
    if (this.isLoading) {
      return;
    }

    void this.showAreaDateTimePickerAndLoadVendors({
      hideAddressPicker: true,
      enableBackdropDismiss: true,
      onlyUpdateResultsIfDataChanged: true,
      trackEvent,
    });
  }

  public onOpenOrderDetailsPage(selectedOrder: ActiveOrderVendor): void {
    void this.overlayService.showLoading().then(() => {
      this.ordersService
        .getOrderDetails(selectedOrder.orderId)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe({
          next: (orderDetails) => {
            void this.overlayService.hideLoading().then(() => {
              const orderVendorDetails = orderDetails.caterers.find((vendor) => vendor.catererId === selectedOrder.vendorId);

              if (orderVendorDetails) {
                void this.modalService.showModal<OrderDetailsModalPageResult>({
                  component: OrderDetailsModalPage,
                  id: OrderDetailsModalPageIdentifier,
                  componentProps: { order: orderDetails, vendor: orderVendorDetails },
                });
              }
            });
          },
          error: (error: HttpErrorResponse) => {
            const errorMessage = this.validationService.getErrorMessage(error);
            this.loggerService.info({
              component: 'VendorsHomePage',
              message: "couldn't get order details",
              details: { error, messageShownToUser: errorMessage },
            });

            void this.overlayService.hideLoading();
            void this.overlayService.showToast({ message: errorMessage, showCloseButton: true, type: 'error' });
          },
        });
    });
  }

  public onPayOnline({ orderId }: ActiveOrderVendor): void {
    void this.overlayService.showLoading().then(() =>
      this.cartService
        .getOrderPaymentOptions(orderId)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe({
          next: (orderPaymentOptions) => {
            void this.modalService
              .showModal<OrderPaymentModalResult>({
                component: OrderPaymentModalPage,
                id: OrderPaymentModalPageIdentifier,
                componentProps: { orderId, orderPaymentOptions },
              })
              .then((result) => {
                if (result?.isSuccess) {
                  this.overlayService.showToast({ type: 'success', message: this.translateService.instant('PAYMENT_SUBMITTED') as string });
                  this.appEventsService.dispatch('OrderPaid');
                } else if (result?.reason) {
                  void this.modalService.showAlert({
                    title: this.translateService.instant('PAYMENT_ERROR_TITLE') as string,
                    message: result.reason,
                  });
                }
              });

            void this.overlayService.hideLoading();
          },
          error: (error: HttpErrorResponse) => {
            const errorMessage = this.validationService.getErrorMessage(error);
            this.loggerService.info({
              component: 'VendorsHomePage',
              message: `couldn't get payment options for order ${orderId}`,
              details: { error, messageShownToUser: errorMessage },
            });

            void this.overlayService
              .hideLoading()
              .then(() => this.overlayService.showToast({ message: errorMessage, showCloseButton: true, type: 'error' }));
          },
        }),
    );
  }

  public onReloadLoyaltyProgramDetails(): void {
    this.reloadLoyaltyProgramDetails();
  }

  public onOpenItemDetailsModal(itemDetailsparams: { vendorId: number; itemId: number; itemServiceType: number }): void {
    this.showItemDetailsModal(itemDetailsparams);
  }

  private handleUrlChanges(fragment: string, queryParams: ParamMap): void {
    const serviceType = fragment ? +fragment : undefined;
    const areaId = this.navigationService.getAreaIdFromParamMap(queryParams);
    const { dateTime, orderForNow } = this.navigationService.getDateTimeAndOrderForNowFromParamMap(queryParams);

    const isServiceTypeDefinedAndValid = serviceType && this.settingsService.isServiceTypeOptionValid(serviceType);

    const serviceTypeHasChanged =
      (this.selectedServiceType && !serviceType) ||
      (!this.selectedServiceType && isServiceTypeDefinedAndValid) ||
      (this.selectedServiceType && isServiceTypeDefinedAndValid && this.selectedServiceType !== serviceType);

    if (serviceTypeHasChanged) {
      this.updateLocalStateBasedOnServiceTypeChangedFromUrl(serviceType);
    }

    const serviceTypeChangedButNotValid =
      serviceType && this.selectedServiceType !== serviceType && !this.settingsService.isServiceTypeOptionValid(serviceType);

    // Manually changing the fragment doesn't trigger a full reload so the guards
    // are not executed — because of that we need to update the URL here to make it
    // valid again
    if (serviceTypeChangedButNotValid) {
      this.navigationService.updateServiceTypeAreaDateTimeFromUrl(
        this.activatedRoute,
        this.selectedServiceType,
        areaId,
        dateTime,
        orderForNow,
      );
      return;
    }

    const addressHasChanged = areaId !== this.selectedAddress?.area.areaId;
    const maybeHandleAreaChangedFromUrl = addressHasChanged ? this.updateLocalStateBasedOnAreaIdChangedFromUrl(areaId) : Promise.resolve();

    void maybeHandleAreaChangedFromUrl.then(() => {
      const orderForNowHasChanged = !!orderForNow !== !!this.isOrderForNowSelected;
      const dateTimeHasChanged = this.dateTimeService.hasChangedDateOrTime(this.selectedDateTime, dateTime);

      if (dateTimeHasChanged || orderForNowHasChanged) {
        this.updateLocalStateBasedOnDateTimeChangeFromUrl(dateTime, orderForNow);
      }

      if (serviceTypeHasChanged || addressHasChanged || dateTimeHasChanged || orderForNowHasChanged) {
        void this.loadVendors({ scrollToTopOfResults: true });
        return;
      }
    });
  }

  private updateLocalStateBasedOnServiceTypeChangedFromUrl(serviceType: number): void {
    let selectedServiceTypeFilterElement: HTMLElement;

    if (serviceType) {
      const serviceTypeIndex = this.serviceTypeOptions.findIndex((serviceTypeOption) => serviceTypeOption.serviceTypes === serviceType);

      if (serviceTypeIndex !== -1) {
        selectedServiceTypeFilterElement = this.serviceTypeFilters.toArray()[serviceTypeIndex]?.nativeElement;
      }
    }

    // We need to set the isLoading property here otherwise the active orders
    // will be shown before we actually change the selected service type
    // https://bilbayt.atlassian.net/browse/CA-2137
    this.isLoading = true;

    if (!this.isHeaderExpanded) {
      this.expandHeader();
    }

    this.selectedServiceType = serviceType;
    this.selectedServiceTypeName = this.settingsService.getServiceTypeName(this.selectedServiceType);
    this.isSelectedServiceTypeBasedOnDateTime = this.settingsService.isServiceTypeBasedOnDateTime(this.selectedServiceType);
    this.isSelectedServiceTypeDeliveredByBilbayt = this.settingsService.isServiceTypeDeliveredByBilbayt(this.selectedServiceType);
    this.selectedServiceTypeSupportsVendorsItemsOnHomePage = this.settingsService.supportsVendorsItemsOnHomePage(this.selectedServiceType);
    this.showHeaderDateTimeButton = this.isSelectedServiceTypeBasedOnDateTime;

    const searchFilters = this.vendorsService.getDatetimeSearchFilters(serviceType);
    this.selectedDateTime = searchFilters.dateTime ?? null;
    this.isOrderForNowSelected = searchFilters.orderForNow ?? null;

    this.analyticsService.trackServiceTypeSelectedCleverTapEvent(serviceType);
    void this.scrollService.scrollHorizontallyToSelectedTab(selectedServiceTypeFilterElement);
  }

  private updateLocalStateBasedOnAreaIdChangedFromUrl(areaIdFromUrl: number): Promise<void> {
    const addressFromSearchFilters = this.vendorsService.getAddressSearchFilters();

    if (!areaIdFromUrl) {
      this.selectedAddress = undefined;
      this.vendorsService.setAddressSearchFilters(undefined);
      return Promise.resolve();
    }

    if (addressFromSearchFilters && addressFromSearchFilters.area.areaId === areaIdFromUrl) {
      this.selectedAddress = addressFromSearchFilters;
      return Promise.resolve();
    }

    return Promise.all([
      toPromise(this.areasService.getAreaById(areaIdFromUrl)),
      this.accountService.isLoggedIn() ? toPromise(this.accountService.getUserSavedAddresses(true)) : Promise.resolve([] as Array<Address>),
    ]).then(([area, savedAddresses]) => {
      if (savedAddresses && savedAddresses.length) {
        const existingAddress = savedAddresses.find((address) => address.area.areaId === areaIdFromUrl);

        if (existingAddress) {
          this.selectedAddress = existingAddress;
          this.vendorsService.setAddressSearchFilters(existingAddress);
          return;
        }
      }

      if (area) {
        const newAddress = this.addressService.createNewAddressFromArea(area);
        this.selectedAddress = newAddress;
        this.vendorsService.setAddressSearchFilters(newAddress);
        return;
      }
    });
  }

  private updateLocalStateBasedOnDateTimeChangeFromUrl(dateTime: string, orderForNow: boolean): void {
    this.selectedDateTime = dateTime;
    this.isOrderForNowSelected = orderForNow;
    this.vendorsService.setDateTimeSearchFilters(this.selectedServiceType, { dateTime, orderForNow });
  }

  private updateWishlistsCollection(): Promise<void> {
    return new Promise((resolve) => {
      this.accountService
        .getUserWishlists()
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe({
          next: (wishlists) => {
            this.collections = [
              ...this.collections.map((collection) => {
                if (collection.type !== HighlightedCollectionType.Wishlists) {
                  return collection;
                }

                return {
                  ...collection,
                  items: [
                    ...wishlists.map((wishlist) => ({
                      genericDetails: { id: wishlist.wishListId, name: wishlist.title, description: wishlist.description },
                    })),
                  ],
                };
              }),
            ];

            resolve();
          },
          error: (_error: HttpErrorResponse) => {
            resolve();
          },
        });
    });
  }

  private updateLoyaltyProgramDetails(): void {
    this.loyaltyProgramDetails = this.shouldShowLoyaltyProgramDetails ? this.loyaltyProgramService.getLoyaltyProgramDetails() : null;
    this.orderCountOnLoyaltyProgramStatusInit = this.upcomingOrdersVendorsCount;
    this.isLoggedInOnLoyaltyProgramStatusInit = this.accountService.isLoggedIn();
  }

  private reloadLoyaltyProgramDetails(): void {
    this.isReloadingLoyaltyProgramDetails = true;

    // Keep the spinning icon for at least 600ms otherwise it may look like a glitch
    forkJoin([this.loyaltyProgramService.loadLoyaltyProgramDetails(), of(true).pipe(delay(600), take(1))])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: ([loyaltyProgramDetails]) => {
          this.loyaltyProgramDetails = loyaltyProgramDetails;
          this.orderCountOnLoyaltyProgramStatusInit = this.upcomingOrdersVendorsCount;
          this.isLoggedInOnLoyaltyProgramStatusInit = this.accountService.isLoggedIn();
          this.isReloadingLoyaltyProgramDetails = false;
        },
        error: (error: HttpErrorResponse) => {
          this.isReloadingLoyaltyProgramDetails = false;
          this.loggerService.info({
            component: 'VendorsHomePage',
            message: "couldn't update loyalty program status",
            details: { error },
          });
        },
      });
  }

  private updateAndSortResultsBasedOnSelectedFilters(): void {
    this.infoMessages = this.vendorsService.getInfoMessagesOfServiceType(this.selectedServiceType);
    this.banners = this.vendorsService.getBannersOfServiceType(this.selectedServiceType);
    this.collections = this.vendorsService.getCollectionsOfServiceType(this.selectedServiceType);

    this.currentPageNumber = 0;

    const vendorsFromSelectedServiceType = this.vendorsService.getVendorsOfServiceType(this.selectedServiceType);
    const vendorsItemsFromSelectedServiceType = this.vendorsService.getVendorsItemsOfServiceType(this.selectedServiceType);

    if (!vendorsFromSelectedServiceType?.length && !vendorsItemsFromSelectedServiceType?.length) {
      this.filteredResults = this.visibleResults = { vendors: [], vendorsItems: [] };
      void this.initializelazyLoadingImagesObserver();
      return;
    }

    this.allResults = { vendors: vendorsFromSelectedServiceType, vendorsItems: vendorsItemsFromSelectedServiceType };
    const filteredResults = this.allResults;

    const selectedTags = this.tags.filter((tag) => tag.selected);

    if (selectedTags?.length) {
      filteredResults.vendors = this.vendorsService.filterBasedOnTags(filteredResults.vendors, selectedTags);
      filteredResults.vendorsItems = this.vendorsService.filterBasedOnTags(filteredResults.vendorsItems, selectedTags);
    }

    filteredResults.vendors.sort((a, b) => a.sorters[this.selectedSorterType] - b.sorters[this.selectedSorterType]);
    filteredResults.vendorsItems.sort((a, b) => a.sorters[this.selectedSorterType] - b.sorters[this.selectedSorterType]);

    this.filteredResults = filteredResults;
    this.visibleResults = this.getNextPageOfResults(filteredResults);

    if (this.visibleResults?.vendors?.length || this.visibleResults?.vendorsItems?.length) {
      void this.initializelazyLoadingImagesObserver();
    }
  }

  private maybeHandleDeepLinks(): boolean {
    if (!this.isBrowser) {
      return true;
    }

    const pendingPushNotification = this.pushNotificationsService.getPendingNotificationToBeHandledInSearchResultsPage();
    if (pendingPushNotification) {
      this.handlePushNotificationDeepLink(pendingPushNotification);
      return false;
    }

    const pendingUniversalLink = this.universalLinksService.getPendingUniversalLinkToBeHandledInSearchResultsPage();
    if (pendingUniversalLink) {
      this.handleUniversalLinkDeepLink(pendingUniversalLink);
      return false;
    }

    if (
      this.universalLinksService.getPendingUniversalLinkToBeHandledInAccountPage() ||
      this.pushNotificationsService.getPendingNotificationToBeHandledInAccountPage()
    ) {
      void this.navigationService.navigateTo('/account');
      return false;
    }

    return true;
  }

  private handlePushNotificationDeepLink(pushNotification: PushNotification): void {
    if (!this.isBrowser) {
      return;
    }

    this.pushNotificationsService.clearPendingPushNotification();

    const targetPage = this.pushNotificationsService.getValidTargetPage(pushNotification);
    const vendorId = pushNotification.catererId;
    const itemId = pushNotification.menuItemId;
    const wishlistId = pushNotification.wishlistId;
    const marketingCollectionId = pushNotification.marketingCollectionId;
    const serviceTypes = pushNotification.serviceTypes;

    if (targetPage === TargetPage.WishlistDetails) {
      this.deepLinkService.openWishlistDetailsPage(wishlistId);
      return;
    }

    if (targetPage === TargetPage.CatererDetails || targetPage === TargetPage.MenuItemDetails) {
      this.deepLinkService.openVendorDetailsPage({ serviceTypes, vendorId, itemId });
      return;
    }

    if (targetPage === TargetPage.ServiceType && this.settingsService.isServiceTypeValid(serviceTypes)) {
      this.onServiceTypeChange(pushNotification.serviceTypes);
      return;
    }

    if (targetPage === TargetPage.MarketingCollectionPage) {
      this.deepLinkService.openMarketingCollectionDetailsPage(marketingCollectionId);
      return;
    }

    if (targetPage === TargetPage.TopSearchedServiceType) {
      const serviceType = this.deepLinkService.getTopSearchedServiceType();

      if (serviceType && this.settingsService.isServiceTypeValid(serviceType)) {
        this.onServiceTypeChange(serviceType);
      }

      return;
    }

    if (targetPage === TargetPage.TopSearchedVendor) {
      const topVendorId = this.deepLinkService.getTopSearchedVendor();

      if (topVendorId) {
        this.deepLinkService.openVendorDetailsPage({ serviceTypes, vendorId: topVendorId });
      }

      return;
    }

    if (targetPage === TargetPage.Cart) {
      this.deepLinkService.openCartPage();
      return;
    }

    if (targetPage === TargetPage.PushNotificationsPermission) {
      this.pushNotificationsService.askForPushNotificationsPermissionEvenIfRejected();
      return;
    }
  }

  private handleUniversalLinkDeepLink(universalLink: UniversalLink): void {
    if (!this.isBrowser) {
      return;
    }

    this.universalLinksService.clearPendingUniversalLink();

    const targetPage = this.universalLinksService.getValidTargetPage(universalLink);
    const vendorId = universalLink.catererId;
    const itemId = universalLink.menuItemId;
    const marketingCollectionId = universalLink.marketingCollectionId;
    const wishlistId = universalLink.wishlistId;
    const serviceTypes = universalLink.serviceTypes;

    if (targetPage === TargetPage.WishlistDetails) {
      this.deepLinkService.openWishlistDetailsPage(wishlistId);
      return;
    }

    if (targetPage === TargetPage.CatererDetails || targetPage === TargetPage.MenuItemDetails) {
      this.deepLinkService.openVendorDetailsPage({ serviceTypes, vendorId, itemId });
      return;
    }

    if (targetPage === TargetPage.ServiceType && this.settingsService.isServiceTypeValid(serviceTypes)) {
      this.onServiceTypeChange(universalLink.serviceTypes);
      return;
    }

    if (targetPage === TargetPage.MarketingCollectionPage) {
      this.deepLinkService.openMarketingCollectionDetailsPage(marketingCollectionId);
      return;
    }

    if (targetPage === TargetPage.TopSearchedServiceType) {
      const serviceType = this.deepLinkService.getTopSearchedServiceType();

      if (serviceType && this.settingsService.isServiceTypeValid(serviceType)) {
        this.onServiceTypeChange(serviceType);
      }

      return;
    }

    if (targetPage === TargetPage.TopSearchedVendor) {
      const topVendorId = this.deepLinkService.getTopSearchedVendor();

      if (topVendorId) {
        this.deepLinkService.openVendorDetailsPage({ serviceTypes, vendorId: topVendorId });
      }

      return;
    }

    if (targetPage === TargetPage.Cart) {
      this.deepLinkService.openCartPage();
      return;
    }

    if (targetPage === TargetPage.PushNotificationsPermission) {
      this.pushNotificationsService.askForPushNotificationsPermissionEvenIfRejected();
      return;
    }
  }

  private showAreaDateTimePickerAndLoadVendors(params?: {
    hideAddressPicker?: boolean;
    enableBackdropDismiss?: boolean;
    onlyUpdateResultsIfDataChanged?: boolean;
    trackEvent?: boolean;
  }): void {
    const maybeSelectAddress = this.selectedAddress ? Promise.resolve(this.selectedAddress) : this.showAreaModal();

    void maybeSelectAddress.then((selectedAddress) => {
      if (!selectedAddress) {
        return;
      }

      void this.showAreaDateTimePicker(params).then((areaDateTimeResults) => {
        if (areaDateTimeResults) {
          areaDateTimeResults = {
            ...areaDateTimeResults,
            selectedAddress,
            hasChangedData:
              areaDateTimeResults.hasChangedData || this.addressService.hasLocationChanged(selectedAddress, this.selectedAddress),
          };
        }

        const dataHasChanged = areaDateTimeResults?.hasChangedData;
        const onlyUpdateResultsIfDataChanged = params?.onlyUpdateResultsIfDataChanged;

        if ((!onlyUpdateResultsIfDataChanged || dataHasChanged) && areaDateTimeResults) {
          const address = areaDateTimeResults.selectedAddress;
          const dateTime = areaDateTimeResults.selectedDateTime;
          const orderForNow = areaDateTimeResults.isOrderForNowSelected;

          this.vendorsService.setAddressSearchFilters(address);
          this.vendorsService.setDateTimeSearchFilters(this.selectedServiceType, { dateTime, orderForNow });
          this.navigationService.updateServiceTypeAreaDateTimeFromUrl(
            this.activatedRoute,
            this.selectedServiceType,
            address.area.areaId,
            dateTime,
            orderForNow,
          );
        }
      });
    });
  }

  private showAreaModal(): Promise<Address | void> {
    return this.modalService.showCard<Address>({
      component: AreaSelectionModalPage,
      id: AreaSelectionModalPageIdentifier,
      presentingElement: this.elementRef.nativeElement,
      componentProps: {
        showCloseButton: true,
        selectedAddress: this.selectedAddress,
      },
    });
  }

  private showAreaDateTimePicker(params?: {
    enableBackdropDismiss?: boolean;
    trackEvent?: boolean;
    hideAddressPicker?: boolean;
  }): Promise<AreaDateTimePickerComponentResult> {
    return this.overlayService
      .showAreaDateTimePicker({
        hideAddressPicker: true,
        enableBackdropDismiss: params?.enableBackdropDismiss ?? true,
        isOrderForNowAvailable: this.settingsService.isOrderForNowAvailableInServiceType(this.selectedServiceType),
        selectedAddress: this.selectedAddress,
        selectedDateTime: this.selectedDateTime,
        isOrderForNowSelected: this.isOrderForNowSelected,
      })
      .then((results) => {
        if (params?.trackEvent) {
          void this.analyticsService.trackEvent({
            name: results ? this.analyticsConfig.eventName.areaDateTimePickerSelect : this.analyticsConfig.eventName.areaDateTimePickerSkip,
            data: {
              [this.analyticsConfig.propertyName.serviceType]: this.settingsService.getServiceTypeNameKey(this.selectedServiceType),
              [this.analyticsConfig.propertyName.areaId]: this.selectedAddress?.area.areaId,
              [this.analyticsConfig.propertyName.dateTime]: results?.selectedDateTime ?? null,
              [this.analyticsConfig.propertyName.bilbaytNow]: results?.isOrderForNowSelected ?? false,
            },
            includeAreaDateTime: true,
          });
        }

        return results;
      });
  }

  private initializeSorterTypeAndTags(): void {
    this.updateSorterType(this.vendorsService.getRecommendedSorterType());
    this.initializePopularTags(this.vendorsService.getPopularTagsOfServiceTypes(this.selectedServiceType));
    this.updateTags(this.vendorsService.getTagsOfServiceTypes(this.selectedServiceType));
  }

  private initializePopularTags(popularTags: Array<Tag>): void {
    if (!popularTags?.length) {
      this.popularTags = { firstRow: [], secondRow: [] };
      return;
    }

    popularTags.push({ tagId: MORE_FILTERS_POPULAR_TAG_ID, name: '' } as Tag);

    if (popularTags.length < POPULAR_TAGS_MIN_QUANTITY_FOR_TWO_ROWS) {
      this.popularTags = { firstRow: popularTags, secondRow: [] };
    } else {
      this.popularTags = { firstRow: [], secondRow: [] };

      const popularTagSpacingLength = 10;
      const popularTagsNamesTotalLength =
        popularTags.reduce((t, tag) => t + tag.name.length, 0) + popularTagSpacingLength * popularTags.length;

      let currentTagNameLenght = 0;

      popularTags.forEach((tag) => {
        if (currentTagNameLenght <= popularTagsNamesTotalLength / 2) {
          this.popularTags.firstRow.push(tag);
          currentTagNameLenght += tag.name.length + popularTagSpacingLength;
        } else {
          this.popularTags.secondRow.push(tag);
        }
      });

      const popularTagsNamesFirstRowLength = this.popularTags.firstRow.reduce((t, tag) => t + tag.name.length + popularTagSpacingLength, 0);
      const popularTagsNamesSecondRowLength = this.popularTags.secondRow.reduce(
        (t, tag) => t + tag.name.length + popularTagSpacingLength,
        0,
      );
      const popularTagsNamesDiff = Math.abs(popularTagsNamesFirstRowLength - popularTagsNamesSecondRowLength);

      if (
        popularTagsNamesFirstRowLength > popularTagsNamesSecondRowLength &&
        this.popularTags.firstRow[this.popularTags.firstRow.length - 1].name.length + popularTagSpacingLength <= popularTagsNamesDiff
      ) {
        this.popularTags.secondRow.unshift(this.popularTags.firstRow.pop());
      }
    }
  }

  private updateSorterType(sorterType: VendorSorterType): void {
    this.selectedSorterType = sorterType;
    this.isRecommendedSorterSelected = sorterType === this.vendorsService.getRecommendedSorterType();
  }

  private updateTags(newTags: Array<Tag>): void {
    this.tags = newTags;

    if (this.popularTags?.firstRow?.length) {
      this.popularTags.firstRow.forEach((popularTag) => {
        popularTag.selected = !!this.tags.find((tag) => tag.tagId === popularTag.tagId)?.selected;
      });
    }

    if (this.popularTags?.secondRow?.length) {
      this.popularTags.secondRow.forEach((popularTag) => {
        popularTag.selected = !!this.tags.find((tag) => tag.tagId === popularTag.tagId)?.selected;
      });
    }

    this.selectedTagsIds = this.tags.filter((tag) => tag.selected).map((tag) => tag.tagId);
    this.hasSelectedTags = this.selectedTagsIds.length > 0;
  }

  private getNextPageOfResults(allResults: VendorsOrItems): VendorsOrItems {
    if (!allResults) {
      return { vendors: [], vendorsItems: [] };
    }

    const results = {
      vendors: allResults.vendors?.length
        ? allResults.vendors.slice(this.currentPageNumber * VENDORS_PAGE_SIZE, (this.currentPageNumber + 1) * VENDORS_PAGE_SIZE)
        : [],
      vendorsItems: allResults.vendorsItems?.length
        ? allResults.vendorsItems.slice(this.currentPageNumber * VENDORS_PAGE_SIZE, (this.currentPageNumber + 1) * VENDORS_PAGE_SIZE)
        : [],
    };

    this.currentPageNumber++;

    return results;
  }

  private maybeHandleAppBeingInBackgroundForTooLong(): boolean {
    const minutesInBackground = this.backgroundService.getMinutesSpentInBackground();
    this.backgroundService.resetMinutesSpentInBackground();

    const shouldUpdateResults = this.isOrderForNowSelected
      ? minutesInBackground >= MIN_TIME_TO_REFRESH_RESULTS_FOR_NOW_IN_MINUTES
      : minutesInBackground >= MIN_TIME_TO_REFRESH_RESULTS_FOR_LATER_IN_MINUTES;

    if (shouldUpdateResults) {
      void this.loadVendors({ scrollToTopOfResults: true });
      return false;
    }

    return true;
  }

  private maybeUpdateResultsAfterAddingItemToCart(): boolean {
    const vendorId = this.vendorsService.getUpdateSearchResultsBasedOnDateTimeFromVendorId();

    if (vendorId) {
      this.vendorsService.clearUpdateSearchResultsBasedOnDateTimeFromVendorId();

      // Since we updated the date/time when adding something to the cart
      // we need to get the date/time from the order and not from the search
      // filters in the VendorsService
      const selectedDateTime = this.cartService.getSelectedDateTimeForVendor(vendorId) ?? null;
      const isOrderForNowSelected = this.cartService.isBilbaytNowCart();

      if (!isOrderForNowSelected && !selectedDateTime) {
        // It may happen that the user added something to the cart but then placed the
        // order which cleared the cart so in that case we don't need to update anything
        return true;
      }

      // The item could've been added from the shortcuts section or from the global
      // search so we should update the search filters for that specific service type
      // and based on that decide if we need to reload this page or not
      const itemServiceType = this.cartService.getItemsServiceTypesFromVendor(vendorId)[0];

      if (itemServiceType) {
        this.vendorsService.setDateTimeSearchFilters(itemServiceType, { dateTime: selectedDateTime, orderForNow: isOrderForNowSelected });
        const { dateTime, orderForNow } = this.vendorsService.getDatetimeSearchFilters(this.selectedServiceType);

        const orderForNowHasChanged = !!orderForNow !== !!this.isOrderForNowSelected;
        const dateTimeHasChanged = this.dateTimeService.hasChangedDateOrTime(this.selectedDateTime, dateTime);

        if (dateTimeHasChanged || orderForNowHasChanged) {
          this.navigationService.updateDateTimeFromUrl(this.activatedRoute, dateTime, orderForNow);
          return false;
        }
      }
    }

    return true;
  }

  private initializelazyLoadingImagesObserver(): Promise<void> {
    return this.platformService.wait(100).then(() => {
      const bannersScrollElement = this.bannersComponent?.getScrollElement() || undefined;

      if (bannersScrollElement) {
        const observerId = 'VendorsPage-BannersLazyLoadingImages';

        this.lazyLoadingImagesObserversIds.push(observerId);
        this.lazyLoadingImagesService.initializeObserver({
          observerId,
          horizontalScroll: {
            parentScrollElement: this.scrollElement,
            horizontalScrollElement: bannersScrollElement,
          },
        });
      }

      if (this.featuredCollectionsComponents) {
        this.featuredCollectionsComponents.toArray().forEach((featuredCollectionComponent, index) => {
          const featuredCollectionScrollElement = featuredCollectionComponent?.getScrollElement() || undefined;
          const observerId = `VendorsPage-CollectionsLazyLoadingImages-${index}`;

          if (featuredCollectionScrollElement) {
            this.lazyLoadingImagesObserversIds.push(observerId);
            this.lazyLoadingImagesService.initializeObserver({
              observerId,
              horizontalScroll: {
                parentScrollElement: this.scrollElement,
                horizontalScrollElement: featuredCollectionScrollElement,
              },
            });
          }
        });
      }

      if (this.resultsElement?.nativeElement) {
        const observerId = 'VendorsPage-VendorsLazyLoadingImages';

        this.lazyLoadingImagesObserversIds.push(observerId);
        this.lazyLoadingImagesService.initializeObserver({
          observerId,
          verticalScroll: {
            // We need to send both the scrollElement and the imageContainerElement
            // because the scroll container also includes some other images that we
            // don't want to handle with this observer like banners and featured lists
            scrollElement: this.scrollElement,
            imagesContainerElement: this.resultsElement.nativeElement,
          },
        });
      }
    });
  }

  private loadVendors({ scrollToTopOfResults } = { scrollToTopOfResults: false }): Promise<boolean> {
    this.isLoading = true;
    this.currentPageNumber = 0;

    const maybeScrollToTop = scrollToTopOfResults ? this.scrollToTop() : Promise.resolve();

    return maybeScrollToTop.then(() =>
      this.overlayService.showLoading().then(
        () =>
          new Promise((resolve) => {
            forkJoin([
              this.vendorsService.loadSearchResults({
                serviceType: this.selectedServiceType,
                area: this.selectedAddress?.area,
                dateTime: this.selectedDateTime,
                orderForNow: this.isOrderForNowSelected,
              }),
            ])
              .pipe(takeUntil(this.unsubscribe$))
              .subscribe({
                next: () => {
                  this.currentCountryCode = this.settingsService.getCountry().code;
                  this.currentLanguageCode = this.settingsService.getLanguage().value;

                  this.initializeSorterTypeAndTags();
                  this.updateAndSortResultsBasedOnSelectedFilters();
                  this.updateLoyaltyProgramDetails();

                  this.isLoading = false;
                  this.isPageInitialized = true;

                  this.showHeaderFiltersButton = !!this.tags?.length;

                  void this.overlayService.hideLoading();
                  resolve(true);
                },
                error: (error: HttpErrorResponse) => {
                  const errorMessage = this.validationService.getErrorMessage(error);
                  this.loggerService.info({
                    component: 'VendorsHomePage',
                    message: "couldn't load vendors",
                    details: { error, messageShownToUser: errorMessage },
                  });

                  const pageParams: ErrorPageParams = { message: errorMessage };
                  void this.navigationService.navigateTo('/error', true, { state: pageParams });

                  void this.overlayService.hideLoading();
                  resolve(false);
                },
              });
          }),
      ),
    );
  }

  private trackPageView(): void {
    void this.analyticsService.trackView({
      name: this.analyticsConfig.pageName.searchResultsPage,
      data: {
        [this.analyticsConfig.propertyName.areaId]: this.selectedAddress?.area.areaId,
        [this.analyticsConfig.propertyName.dateTime]: this.selectedDateTime ?? null,
        [this.analyticsConfig.propertyName.bilbaytNow]: this.isOrderForNowSelected ?? false,
        [this.analyticsConfig.propertyName.serviceType]: this.selectedServiceType ?? undefined,
      },
    });
  }

  private initializeTemplateElements(scrollElement: HTMLElement): void {
    this.scrollElement = scrollElement;

    this.domController.read(() => {
      const elementRef = this.elementRef.nativeElement;
      this.mainHeaderElement = elementRef.querySelector('.main-header');
      this.headerItemOneElement = this.mainHeaderElement.querySelector('.header-item-one');
      this.headerItemTwoElement = this.mainHeaderElement.querySelector('.header-item-two');
      this.headerItemThreeElement = this.mainHeaderElement.querySelector('.header-item-three');
    });
  }

  private handleScrollEvent(eventDetail: ScrollEventDetail): void {
    const scrollingUp = eventDetail.currentY < this.currentY;
    const scrollingDown = eventDetail.currentY > this.currentY;
    this.currentY = eventDetail.currentY;

    if (this.isBrowser) {
      if (this.selectedServiceTypeSupportsVendorsItemsOnHomePage) {
        this.showViewModeButton = false;
      } else if (scrollingDown && this.scrollElement.scrollTop > SCROLL_SECOND_UI_BREAKPOINT_IN_PX && !this.showViewModeButton) {
        this.showViewModeButton = true;
      } else if (scrollingUp && this.scrollElement.scrollTop <= SCROLL_FIRST_UI_BREAKPOINT_IN_PX && this.showViewModeButton) {
        this.showViewModeButton = false;
      }
    }

    if (!this.mainHeaderElement) {
      return;
    }

    this.domController.read(() => {
      const mainHeaderElementHeight = this.mainHeaderElement?.getBoundingClientRect().height;

      const collectionElements = this.featuredCollectionsComponents?.toArray() || [];
      const lastCollectionElementBottom = collectionElements?.length
        ? collectionElements[collectionElements.length - 1].getScrollElement().getBoundingClientRect().bottom
        : 0;

      this.domController.write(() => {
        if (!this.isBrowser && mainHeaderElementHeight) {
          this.updateUiElementsBasedOnScrollPosition({
            scrollingUp,
            scrollingDown,
            velocityY: eventDetail.velocityY,
            mainHeaderElementHeight,
          });
        }

        if (collectionElements) {
          this.resetCollectionsScrollPosition({ lastCollectionElementBottom });
        }
      });
    });
  }

  private updateUiElementsBasedOnScrollPosition({
    scrollingUp,
    scrollingDown,
    velocityY,
    mainHeaderElementHeight,
  }: {
    scrollingUp: boolean;
    scrollingDown: boolean;
    velocityY: number;
    mainHeaderElementHeight: number;
  }): void {
    const firstBreakPoint = mainHeaderElementHeight + SCROLL_FIRST_UI_BREAKPOINT_IN_PX;
    const secondBreakPoint = mainHeaderElementHeight + SCROLL_SECOND_UI_BREAKPOINT_IN_PX;
    const absoluteVelocity = Math.abs(velocityY);

    if (this.selectedServiceTypeSupportsVendorsItemsOnHomePage) {
      this.showViewModeButton = false;
    } else if (
      scrollingDown &&
      this.scrollElement.scrollTop > secondBreakPoint + SCROLL_THIRD_UI_BREAKPOINT_IN_PX &&
      !this.showViewModeButton
    ) {
      this.showViewModeButton = true;
    } else if (scrollingUp && this.scrollElement.scrollTop <= secondBreakPoint && this.showViewModeButton) {
      this.showViewModeButton = false;
    }

    // We shouldn't collapse the header if there're no popular tags since we're
    // not showing the service type horizontal scroller anymore
    if (!this.popularTags?.firstRow.length && !this.popularTags?.secondRow.length) {
      return;
    }

    if (
      scrollingDown &&
      this.isHeaderExpanded &&
      this.scrollElement.scrollTop > secondBreakPoint &&
      absoluteVelocity > HEADER_COLLAPSING_VELOCITY_LIMIT
    ) {
      this.collapseHeader();
    } else if (
      ((scrollingUp && absoluteVelocity > HEADER_EXPANDING_VELOCITY_LIMIT) || this.scrollElement.scrollTop <= firstBreakPoint) &&
      !this.isHeaderExpanded
    ) {
      this.expandHeader();
    }
  }

  private resetCollectionsScrollPosition({ lastCollectionElementBottom }: { lastCollectionElementBottom: number }): void {
    if (this.featuredCollectionsComponents?.toArray().length) {
      const lastCollectionBottom = lastCollectionElementBottom + 32;

      // Reset featured collections scroll position
      if (lastCollectionBottom <= 0 && this.resetFeaturedItemHorizontalScroll) {
        this.featuredCollectionsComponents.toArray().forEach((element) => element.resetHorizontalScroll());
        this.resetFeaturedItemHorizontalScroll = false;
      } else if (lastCollectionBottom > 0 && !this.resetFeaturedItemHorizontalScroll) {
        this.resetFeaturedItemHorizontalScroll = true;
      }
    }
  }

  private expandHeader(): void {
    this.isHeaderExpanded = true;

    this.domController.write(() => {
      // Show the header items
      this.headerItemOneElement.style.opacity = this.headerItemTwoElement.style.opacity = '1';
      this.headerItemOneElement.style.pointerEvents = this.headerItemTwoElement.style.pointerEvents = '';

      // Restore the entire header
      this.mainHeaderElement.style.transform = 'translateY(0) translateZ(0px)';
    });
  }

  private collapseHeader(): void {
    this.isHeaderExpanded = false;

    this.domController.read(() => {
      const headerItemOneTop = this.headerItemOneElement.getBoundingClientRect().top;
      const headerItemThreeTop = this.headerItemThreeElement.getBoundingClientRect().top;

      // Keep the inset-top (16 is the margin top of the service types section)
      const distance = headerItemThreeTop - headerItemOneTop + 16;

      this.domController.write(() => {
        // Hide the header items
        this.headerItemOneElement.style.opacity = this.headerItemTwoElement.style.opacity = '0';
        this.headerItemOneElement.style.pointerEvents = this.headerItemTwoElement.style.pointerEvents = 'none';

        // Move the entire header
        this.mainHeaderElement.style.transform = `translateY(-${distance}px) translateZ(0px)`;
      });
    });
  }

  private scrollToTop(): Promise<void> {
    return this.scrollElement.scrollTop === 0 ? Promise.resolve() : this.content.scrollToTop(300);
  }

  private initializePage() {
    // Initialize the service type options AFTER the scroll
    // element is ready to avoid some js errors
    // https://bilbayt.atlassian.net/browse/CA-2293

    this.isLoggedIn = this.accountService.isLoggedIn();
    this.serviceTypeOptions = this.settingsService.getServiceTypeOptions();

    // The service type from the URL should be always valid because it's validated
    // by the HomePageVendorsGuard
    this.selectedServiceType = this.navigationService.getServiceTypeFromUrl(this.activatedRoute.snapshot);
    this.selectedServiceTypeName = this.settingsService.getServiceTypeName(this.selectedServiceType);

    void this.content.scrollToTop(300).then(() => {
      void this.content.getScrollElement().then((scrollElement) => {
        this.initializeTemplateElements(scrollElement);

        // Search filters are also updated based on the URL by the HomePageVendorsGuard so
        // we can assume its values are consistent with the current URL
        const address = this.vendorsService.getAddressSearchFilters();
        const { dateTime, orderForNow } = this.vendorsService.getDatetimeSearchFilters(this.selectedServiceType);

        this.selectedAddress = address || null;
        this.selectedDateTime = dateTime || null;
        this.isOrderForNowSelected = orderForNow || null;

        this.isSelectedServiceTypeBasedOnDateTime = this.settingsService.isServiceTypeBasedOnDateTime(this.selectedServiceType);
        this.isSelectedServiceTypeDeliveredByBilbayt = this.settingsService.isServiceTypeDeliveredByBilbayt(this.selectedServiceType);
        this.selectedServiceTypeSupportsVendorsItemsOnHomePage = this.settingsService.supportsVendorsItemsOnHomePage(
          this.selectedServiceType,
        );
        this.showHeaderDateTimeButton = this.isSelectedServiceTypeBasedOnDateTime;
        this.showServiceTypeFiltersScrollButton = this.serviceTypeOptions.length >= 3;

        this.backgroundService.resetMinutesSpentInBackground();

        this.isLoading = true;
        void this.loadVendors({ scrollToTopOfResults: false }).then((canContinue) => {
          this.appService.setHomePageLoaded();

          if (!canContinue) {
            return;
          }

          this.maybeHandleDeepLinks();
        });
      });
    });
  }

  private showItemDetailsModal({ vendorId, itemId, itemServiceType }: { vendorId: number; itemId: number; itemServiceType: number }): void {
    const isGroceryItem = this.settingsService.isServiceTypeBasedOnCategories(itemServiceType);

    if (!isGroceryItem) {
      const isServiceTypeBasedOnTimeSlots = this.settingsService.isServiceTypeBasedOnTimeSlots(itemServiceType);

      const isOrderForNowSelected = this.cartService.isBilbaytNowCart();
      const selectedDateTimeForVendor = this.cartService.getSelectedDateTimeForVendor(vendorId);
      const dateTime = isServiceTypeBasedOnTimeSlots || isOrderForNowSelected ? null : selectedDateTimeForVendor ?? null;

      void this.getMenuVendorDetailsFromApi({
        vendorId,
        itemId,
        itemServiceType,
      }).then((vendorDetails) => {
        if (!vendorDetails) {
          return;
        }

        const itemDetailsParams: MenuItemDetailsModalParams = {
          itemId,
          vendorDetails,
          selectedServiceType: itemServiceType,
          selectedAddress: this.cartService.getAddress(),
          selectedDateTime: dateTime,
          isOrderForNowSelected,
        };

        void this.modalService
          .showModal<MenuItemDetailsModalResult>({
            component: MenuItemDetailsModalComponent,
            id: MenuItemDetailsModalPageIdentifier,
            canDismiss: true,
            setAutoHeightOnBrowser: true,
            componentProps: { itemDetailsParams },
          })
          .then((result) => {
            if (result && result.addedToCart) {
              this.maybeUpdateResultsAfterAddingItemToCart();
            }
          });
      });
    } else {
      void this.getGroceryVendorAndItemDetailsFromApi(vendorId, itemId).then(({ vendorDetails, itemDetails }) => {
        if (!vendorDetails || !itemDetails) {
          return;
        }

        const itemDetailsParams: GroceryItemDetailsModalParams = {
          itemDetails,
          vendorDetails,
          selectedAddress: this.cartService.getAddress(),
        };

        void this.modalService
          .showCard<GroceryItemDetailsModalResult>({
            component: GroceryItemDetailsModalComponent,
            id: GroceryItemDetailsModalPageIdentifier,
            canDismiss: true,
            setAutoHeightOnBrowser: true,
            presentingElement: this.elementRef?.nativeElement ?? undefined,
            componentProps: { itemDetailsParams },
          })
          .then((result) => {
            if (result && result.addedToCart) {
              this.maybeUpdateResultsAfterAddingItemToCart();
            }
          });
      });
    }
  }

  private getMenuVendorDetailsFromApi({
    vendorId,
    itemId,
    itemServiceType,
  }: {
    vendorId: number;
    itemId: number;
    itemServiceType: number;
  }): Promise<VendorDetails> {
    return new Promise((resolve) => {
      void this.overlayService.showLoading().then(() => {
        this.vendorsService
          .getVendorDetailsById({
            vendorId,
            itemId,
            areaId: this.selectedAddress?.areaId,
            dateTime: this.selectedDateTime,
            orderForNow: this.isOrderForNowSelected,
            serviceTypes: itemServiceType,
          })
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe({
            next: (vendorDetails) => {
              resolve(vendorDetails);
              void this.overlayService.hideLoading();
            },
            error: (error: HttpErrorResponse) => {
              void this.overlayService.hideLoading().then(() => {
                const errorMessage = this.validationService.getErrorMessage(error);
                this.loggerService.info({
                  component: 'VendorsHomePage',
                  message: "couldn't get menu vendor details",
                  details: { error, messageShownToUser: errorMessage },
                });

                this.overlayService.showToast({ type: 'error', showCloseButton: true, message: errorMessage });
                resolve(null);
              });
            },
          });
      });
    });
  }

  private getGroceryVendorAndItemDetailsFromApi(
    vendorId: number,
    itemId: number,
  ): Promise<{ vendorDetails: GroceryVendorDetails; itemDetails: GroceryMenuItem }> {
    return new Promise((resolve) => {
      void this.overlayService.showLoading().then(() => {
        forkJoin([this.vendorsService.getGroceryVendorDetails(vendorId), this.vendorsService.getGroceryMenuItemDetails(itemId)])
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe({
            next: ([vendorDetails, itemDetails]) => {
              resolve({ vendorDetails, itemDetails });
              void this.overlayService.hideLoading();
            },
            error: (error: HttpErrorResponse) => {
              void this.overlayService.hideLoading().then(() => {
                const errorMessage = this.validationService.getErrorMessage(error);
                this.loggerService.info({
                  component: 'HomeVendorsPage',
                  message: "couldn't get grocery item details",
                  details: { error, messageShownToUser: errorMessage },
                });

                this.overlayService.showToast({ type: 'error', showCloseButton: true, message: errorMessage });
                resolve({ vendorDetails: null, itemDetails: null });
              });
            },
          });
      });
    });
  }
}
